/**
 * The unit picked for each trait column of the data entry grids, kept in local storage.
 *
 * Shared by both frontends, so both grids read and write the same picks. Keep it dependency-free:
 * the legacy `tsc` compiles it under its own config.
 */

// Picks are grouped by trial, so a grid only sends the picks of the trial it shows.
const STORAGE_KEY = 'unitPreferencesByTrial';
// Picks stored before they were grouped by trial; they no longer apply and are removed on write.
const LEGACY_STORAGE_KEY = 'unitPreferencesForTraits';

type UnitIdByTraitId = Record<number, number>;

/** Read the unit picked for each trait of a trial, as unit id by trait id. */
export function getUnitPreferencesForTraits(trialId: string | number): UnitIdByTraitId {
  return readUnitPreferencesByTrial()[String(trialId)] ?? {};
}

export function setUnitPreferenceForTrait(
  trialId: string | number,
  traitId: number,
  unitId: number
): void {
  const unitPreferencesByTrial = readUnitPreferencesByTrial();
  unitPreferencesByTrial[String(trialId)] = {
    ...unitPreferencesByTrial[String(trialId)],
    [traitId]: unitId,
  };
  writeUnitPreferencesByTrial(unitPreferencesByTrial);
}

/** Forget the unit picked for a trait, so its column opens in its default unit again. */
export function removeUnitPreferenceForTrait(trialId: string | number, traitId: number): void {
  const unitPreferencesByTrial = readUnitPreferencesByTrial();
  unitPreferencesByTrial[String(trialId)] = Object.fromEntries(
    Object.entries(unitPreferencesByTrial[String(trialId)] ?? {}).filter(
      ([storedTraitId]) => Number(storedTraitId) !== traitId
    )
  );
  writeUnitPreferencesByTrial(unitPreferencesByTrial);
}

function readUnitPreferencesByTrial(): Record<string, UnitIdByTraitId> {
  if (typeof window === 'undefined') {
    return {};
  }

  let parsed: unknown;
  try {
    parsed = JSON.parse(window.localStorage.getItem(STORAGE_KEY) ?? '{}');
  } catch {
    return {};
  }
  if (!isPlainObject(parsed)) {
    return {};
  }

  const unitPreferencesByTrial: Record<string, UnitIdByTraitId> = {};
  for (const [trialId, stored] of Object.entries(parsed)) {
    if (!isPlainObject(stored)) {
      continue;
    }
    const unitIdByTraitId: UnitIdByTraitId = {};
    for (const [traitId, unitId] of Object.entries(stored)) {
      if (
        Number.isInteger(Number(traitId)) &&
        typeof unitId === 'number' &&
        Number.isInteger(unitId)
      ) {
        unitIdByTraitId[Number(traitId)] = unitId;
      }
    }
    unitPreferencesByTrial[trialId] = unitIdByTraitId;
  }
  return unitPreferencesByTrial;
}

function writeUnitPreferencesByTrial(
  unitPreferencesByTrial: Record<string, UnitIdByTraitId>
): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(unitPreferencesByTrial));
    window.localStorage.removeItem(LEGACY_STORAGE_KEY);
  } catch {
    // Storage can be full or blocked; the pick is then not kept.
  }
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}
